import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import type { Params } from '@angular/router';
import { map, type Observable, tap } from 'rxjs';
import { take } from 'rxjs/operators';

import type { ServiceOffering, TermAndCondition } from '@core/models/app.model';
import type { IPaginatedResponse, IPaginationParams } from '@core/models/pagination-params.model';
import { MediatorService } from '@core/services/mediator.service';
import { adminUrls } from '@shared/domain/admin-urls';
import { servicesUrls } from '@shared/domain/services-urls';

import type { IPendingServiceRequest } from '../../../provider/models/provider.model';
import type {
  ICompanyServiceOffering,
  IServiceOfferingConfiguration,
  IServiceOfferingMetadata,
  IServiceSubscription,
  ServiceLogicalStatus,
} from '../models/services.model';

@Service()
export class ServicesManagementService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly mediatorService: MediatorService = inject(MediatorService);

  public getServices(
    status: ServiceLogicalStatus | null
  ): Observable<IServiceOfferingConfiguration[]> {
    let params: HttpParams = new HttpParams();

    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<IServiceOfferingConfiguration[]>(servicesUrls.getServices(), { params });
  }

  public reorder(
    offerings: IServiceOfferingConfiguration[]
  ): Observable<IServiceOfferingConfiguration[]> {
    return this.http.post<IServiceOfferingConfiguration[]>(
      servicesUrls.reorderServices(),
      offerings
    );
  }

  public getService(offeringId: string): Observable<IServiceOfferingMetadata> {
    return this.http.get<IServiceOfferingMetadata>(servicesUrls.getService(offeringId));
  }

  public updateField(
    offeringId: string,
    field: Partial<IServiceOfferingMetadata>
  ): Observable<IServiceOfferingMetadata> {
    return this.http.patch<IServiceOfferingMetadata>(
      servicesUrls.patchServiceField(offeringId),
      field
    );
  }

  public undo(offeringId: string): Observable<void> {
    return this.http.post<void>(servicesUrls.serviceUndo(offeringId), {});
  }

  public publish(offeringId: string): Observable<void> {
    return this.http.post<void>(servicesUrls.servicePublish(offeringId), {});
  }

  public getCompanyServices(includeHidden?: boolean): Observable<ICompanyServiceOffering[]> {
    let params = new HttpParams();

    if (includeHidden) {
      params = params.set('includeHidden', 'true');
    }

    return this.http.get<ICompanyServiceOffering[]>(servicesUrls.getCompanyServices(), { params });
  }

  public getCompanyService(offeringId: string): Observable<ICompanyServiceOffering> {
    return this.http.get<ICompanyServiceOffering>(servicesUrls.getCompanyService(offeringId));
  }

  public subscribeToService(offeringId: string): Observable<void> {
    return this.http.post<void>(servicesUrls.postSubscribe(offeringId), {});
  }

  public getOnboardingServicesInfo(
    params: Params & IPaginationParams
  ): Observable<IPaginatedResponse<IServiceSubscription[]>> {
    return this.http
      .get<IPaginatedResponse<IServiceSubscription[]>>(servicesUrls.getOnboardingServicesInfo(), {
        params,
      })
      .pipe(
        tap((res: IPaginatedResponse<IServiceSubscription[]>) =>
          this.mediatorService.updateResponseStats(res.stats)
        )
      );
  }

  public refuse(subscriptionId: string): Observable<void> {
    return this.http.post<void>(servicesUrls.postRefuse(subscriptionId), {});
  }

  public activate(subscriptionId: string): Observable<string> {
    return this.http.post<string>(servicesUrls.postActivate(subscriptionId), {});
  }

  public getPendingServiceRequests(): Observable<IPendingServiceRequest[]> {
    return this.http.get<IPendingServiceRequest[]>(adminUrls.getHomePageServiceSubscriptions());
  }

  public getNumberOfPurchaseOrders(): Observable<number> {
    return this.http.get<number>(adminUrls.getHomePagePurchaseOrders());
  }

  /**
   * Get list of terms and conditions for particular offering.
   * @param offeringId Offering id.
   */
  public getServiceTermsAndConditions(offeringId: string): Observable<TermAndCondition[]> {
    return this.http.get<TermAndCondition[]>(servicesUrls.getServiceTermsAndConditions(offeringId));
  }

  public getServiceInfoByOfferingCode$(
    offeringCode: ServiceOffering,
    includeHidden = true
  ): Observable<ICompanyServiceOffering> {
    return this.getCompanyServices(includeHidden).pipe(
      map((services) => {
        const serviceOffering = services.find((service) => service.offeringCode === offeringCode);

        if (!serviceOffering) {
          throw new Error(`Service offering with code "${offeringCode}" was not found`);
        }

        return serviceOffering;
      }),
      take(1)
    );
  }
}
